import { type ReactNode, type RefObject, useCallback } from 'react';
import { faXmark } from '@awesome.me/kit-4a6bf60416/icons/classic/regular';
import type { Measurable } from '@radix-ui/rect';

import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover';
import { Heading5 } from '@/components/ui/typography';
import { usePopoverPlacement } from '@/hooks/use-popover-placement';
import { cn } from '@/lib/utils';

interface SettingsPopoverShellProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  anchorRef?: RefObject<Measurable | null>;
  title: string;
  hasSubModalOpen?: boolean;
  trackMovingAnchor?: boolean;
  onSave: () => void;
  onCancel: () => void;
  children: ReactNode;
}

export function SettingsPopoverShell({
  open,
  onOpenChange,
  anchorRef,
  title,
  hasSubModalOpen = false,
  trackMovingAnchor = false,
  onSave,
  onCancel,
  children,
}: SettingsPopoverShellProps) {
  const { side, sideOffset } = usePopoverPlacement(anchorRef, open);

  const handleSave = useCallback(() => {
    onSave();
    onOpenChange(false);
  }, [onSave, onOpenChange]);

  const handleClose = useCallback(() => {
    onCancel();
    onOpenChange(false);
  }, [onCancel, onOpenChange]);

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next && hasSubModalOpen) return;
        if (!next) handleClose();
      }}
    >
      {anchorRef ? <PopoverAnchor virtualRef={anchorRef as RefObject<Measurable>} /> : null}
      <PopoverContent
        side={side}
        align="start"
        sideOffset={sideOffset}
        avoidCollisions
        collisionPadding={16}
        hideWhenDetached
        updatePositionStrategy={trackMovingAnchor ? 'always' : 'optimized'}
        className={cn(
          'z-[130] w-96 overflow-hidden border bg-background',
          hasSubModalOpen && 'hidden',
        )}
        onPointerDown={(event) => event.stopPropagation()}
        onInteractOutside={(event) => {
          if (hasSubModalOpen) {
            event.preventDefault();
            return;
          }
          const target = event.target;
          if (!(target instanceof Element)) return;
          if (target.closest('[data-slot="select-content"]')) {
            event.preventDefault();
          }
        }}
      >
        <Button
          size="icon"
          variant="ghost"
          colorScheme="secondary"
          onClick={handleClose}
          className="absolute top-4 right-4 z-50 h-8 w-8 bg-background"
        >
          <Icon icon={faXmark} />
          <span className="sr-only">Close</span>
        </Button>

        <div className="flex h-full flex-col">
          <div className="max-h-[min(70vh,700px)] min-h-0 flex-1 space-y-4 overflow-y-auto px-2">
            <Heading5 className="text-base">{title}</Heading5>
            {children}
            <div className="sticky bottom-0 mt-4 flex justify-end space-x-4 bg-background pt-4">
              <Button variant="outline" colorScheme="secondary" onClick={handleClose}>
                Cancel
              </Button>
              <Button onClick={handleSave}>Save Changes</Button>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
